import { useEffect } from 'react';

declare const __APP_VERSION__: string;

/**
 * Проверка версии приложения при старте.
 * Если серверная версия отличается от встроенной — значит деплой произошёл,
 * а браузер загрузил старые бандлы из HTTP Disk Cache.
 * В этом случае: удаляем SW, чистим Cache API, делаем hard reload.
 * 
 * НЕ запускается на маршрутах виджета (/booking/*, /widget/*).
 * При ошибке fetch — молча пропускает (не ломает работу).
 */
export function useVersionCheck() {
  useEffect(() => {
    // Защита виджета: не проверяем версию на маршрутах бронирования
    const path = window.location.pathname;
    if (path.startsWith('/booking/') || path.startsWith('/widget/')) {
      return;
    }

    // В development нет смысла проверять
    if (typeof __APP_VERSION__ === 'undefined') {
      return;
    }

    // Защита от бесконечного reload: если уже делали reload — пропускаем
    const reloadKey = 'version_reload_done';
    const lastReload = sessionStorage.getItem(reloadKey);
    if (lastReload === __APP_VERSION__) {
      return;
    }

    const checkVersion = async () => {
      try {
        const response = await fetch('/version.json', { cache: 'no-store' });
        if (!response.ok) return;

        const data = await response.json();
        const serverVersion = data?.v;

        if (!serverVersion) return;

        // "dev" — это дефолт для preview/localhost, не надо из-за него перезагружать
        if (serverVersion === 'dev') return;

        // Если версии совпадают — всё ок
        if (serverVersion === __APP_VERSION__) return;

        console.log(`[VersionCheck] Обнаружена новая версия: ${serverVersion} (текущая: ${__APP_VERSION__}). Обновляем...`);

        // Помечаем что делаем reload для этой версии (защита от loop)
        sessionStorage.setItem(reloadKey, __APP_VERSION__);

        // 1. Удаляем Service Worker
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          for (const reg of registrations) {
            await reg.unregister();
          }
        }

        // 2. Чистим Cache API
        if ('caches' in window) {
          const names = await caches.keys();
          for (const name of names) {
            await caches.delete(name);
          }
        }

        // 3. Принудительно обновляем index.html в disk cache
        await fetch('/index.html', { cache: 'reload' }).catch(() => {});

        // 4. Hard reload
        window.location.reload();
      } catch {
        // При любой ошибке — молча пропускаем, не ломаем приложение
      }
    };

    // Задержка 5 сек — даём auth инициализироваться ДО проверки версии
    const timer = setTimeout(checkVersion, 5000);
    return () => clearTimeout(timer);
  }, []);
}
